/* =====================================================================
 * AdminCharts — 의존성 없는 경량 SVG/CSS 차트 (owner 대시보드용)
 *
 * 차트 라이브러리 추가 없이 design-system 토큰으로 깔끔하게 그린다.
 *  - LineChart: 시계열 area+line (viewBox 균일 스케일 → 선 두께 왜곡 없음)
 *  - FeatureBars: 가로 막대(div 기반, 반응형)
 * ===================================================================== */

import React from 'react';
import styled from 'styled-components';

export interface ChartPoint {
  label: string;
  value: number;
}

/* ---- LineChart (시계열) ---- */

export const LineChart: React.FC<{ data: ChartPoint[]; color?: string }> = ({
  data,
  color = '#3b82f6',
}) => {
  if (!data || data.length === 0) return null;
  const W = 600;
  const H = 170;
  const padL = 8;
  const padR = 8;
  const padT = 12;
  const padB = 22;
  const n = data.length;
  const max = Math.max(1, ...data.map((d) => d.value));
  const x = (i: number) => (n <= 1 ? W / 2 : padL + (i * (W - padL - padR)) / (n - 1));
  const y = (v: number) => H - padB - (v / max) * (H - padT - padB);
  const pts = data.map((d, i) => `${x(i).toFixed(1)},${y(d.value).toFixed(1)}`);
  const areaPath = `M ${x(0).toFixed(1)},${(H - padB).toFixed(1)} L ${pts.join(' L ')} L ${x(n - 1).toFixed(1)},${(H - padB).toFixed(1)} Z`;
  const gridYs = [0, 0.5, 1];

  return (
    <ChartWrap>
      <Svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        {gridYs.map((g) => {
          const gy = H - padB - g * (H - padT - padB);
          return <line key={g} x1={padL} x2={W - padR} y1={gy} y2={gy} className="grid" />;
        })}
        <path d={areaPath} fill={color} fillOpacity={0.1} />
        <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {n <= 31 && data.map((d, i) => <circle key={i} cx={x(i)} cy={y(d.value)} r={2.4} fill={color} />)}
        <text x={padL} y={padT - 2} className="ymax">{max.toLocaleString()}</text>
      </Svg>
      <Axis>
        <span>{data[0].label}</span>
        {n > 1 && <span>{data[n - 1].label}</span>}
      </Axis>
    </ChartWrap>
  );
};

/* ---- FeatureBars (가로 막대) ---- */

export const FeatureBars: React.FC<{ data: ChartPoint[]; color?: string }> = ({
  data,
  color = '#8b5cf6',
}) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <Bars>
      {data.map((d) => (
        <BarRow key={d.label}>
          <BarLabel title={d.label}>{d.label}</BarLabel>
          <BarTrack>
            <BarFill style={{ width: `${(d.value / max) * 100}%`, background: color }} />
          </BarTrack>
          <BarVal>{d.value.toLocaleString()}</BarVal>
        </BarRow>
      ))}
    </Bars>
  );
};

/* ------------------------------------------------------------------ */

const ChartWrap = styled.div`
  width: 100%;
`;

const Svg = styled.svg`
  width: 100%;
  height: auto;
  display: block;
  .grid {
    stroke: ${({ theme }) => theme.colors.borderLight};
    stroke-width: 1;
    stroke-dasharray: 3 3;
  }
  .ymax {
    fill: ${({ theme }) => theme.colors.textMuted};
    font-size: 11px;
  }
`;

const Axis = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const Bars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 64px;
  gap: 10px;
  align-items: center;
  font-size: 0.8125rem;

  /* 모바일: 라벨/값 컬럼을 좁혀 막대가 충분한 폭을 갖게 한다. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 84px 1fr 52px;
    gap: 8px;
  }
`;

const BarLabel = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BarTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
`;

const BarVal = styled.span`
  text-align: right;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;
